import {Group, Radio, Stack} from '@mantine/core';
import type {FC} from 'react';
import type {ISelectOption} from 'src/types/Form';

/**
 * Labeled radio button group, laid out vertically or horizontally.
 * @registry Labeled single-choice radio group.
 *
 * Ported from `library/elements/RadioGroupBox`, rebuilt on Mantine
 * `Radio.Group` (design/ is Mantine-only) instead of the original MUI
 * `RadioGroup`. `onChangeCallback` is simplified from MUI's
 * `(event: ChangeEvent<HTMLInputElement>, value: string) => void` to
 * `(value: string) => void` — Mantine's `Radio.Group` hands back the value
 * directly and no consumer used the event argument. The unused `fullWidth`
 * prop was dropped (no consumer passed it).
 */
export interface RadioGroupBoxProps {
  label: string;
  name?: string;
  helperText?: string;
  disabled?: boolean;
  error?: boolean;
  options: ISelectOption[];
  value?: string;

  orientation?: 'vertical' | 'horizontal';

  onChangeCallback: (value: string) => void;
}
export const RadioGroupBox: FC<RadioGroupBoxProps> = ({
  label,
  name,
  helperText,
  disabled,
  error,
  options,
  value,
  onChangeCallback,
  orientation = 'vertical',
}) => {
  const Layout = orientation === 'horizontal' ? Group : Stack;

  return (
    <Radio.Group
      label={label}
      description={helperText}
      error={error}
      name={name}
      value={value}
      onChange={onChangeCallback}>
      <Layout mt="xs" gap="xs">
        {options.map(option => (
          <Radio
            key={option.id}
            value={String(option.id)}
            label={option.label}
            disabled={disabled}
          />
        ))}
      </Layout>
    </Radio.Group>
  );
};
